import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Tag,
  Plus
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { useApiOnMount, useApi } from '../../hooks/useApi';
import { secretService } from '../../services/secretService';
import { formatRelativeTime, getSecretTypeIcon, truncateText } from '../../utils/helpers';
import SecretCard from '../../components/UI/SecretCard';
import toast from 'react-hot-toast';

const LabelDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.gray700};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray50};
    border-color: ${({ theme }) => theme.colors.gray400};
  }
`;

const LabelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const LabelIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const LabelDetails = styled.div`
  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  }
  
  p {
    color: ${({ theme }) => theme.colors.gray600};
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex: 1;
  max-width: 500px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    max-width: none;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
`;

const FilterButton = styled(Button)`
  white-space: nowrap;
`;

const SecretsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;


const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['4xl']} ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.gray500};
  
  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
    color: ${({ theme }) => theme.colors.gray700};
  }
  
  p {
    margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing['4xl']};
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid ${({ theme }) => theme.colors.gray200};
    border-top: 3px solid ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function LabelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Get label details
  const { data: label, loading: labelLoading, error: labelError } = useApiOnMount(
    () => secretService.getLabel(id),
    []
  );

  // Get secrets with this label
  const { data: secrets, loading: secretsLoading, error: secretsError, refetch } = useApiOnMount(
    () => secretService.getSecretsByLabel(id),
    [id]
  );

  const filteredSecrets = secrets?.filter(secret =>
    secret.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    secret.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    secret.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleSecretClick = (secretId) => {
    navigate(`/secrets/${secretId}`);
  };

  const handleCopyPassword = async (secretId, e) => {
    e.stopPropagation();
    try {
      const response = await secretService.getDecryptedPassword(secretId);
      await navigator.clipboard.writeText(response.data.password);
      toast.success('Password copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };

  const handleCopySecretKey = async (secretId, e) => {
    e.stopPropagation();
    try {
      const response = await secretService.getDecryptedSecretKey(secretId);
      await navigator.clipboard.writeText(response.data.secret_key);
      toast.success('Secret key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy secret key');
    }
  };

  const handleEditSecret = (secretId) => {
    const secret = filteredSecrets.find(s => s.id === secretId);
    if (secret) {
      navigate(`/secrets/${secretId}`);
    }
  };

  const handleDeleteSecret = async (secretId) => {
    if (window.confirm('Are you sure you want to delete this secret? This action cannot be undone.')) {
      try {
        await secretService.deleteSecret(secretId);
        toast.success('Secret deleted successfully');
        // Dispatch event to refresh label counts
        window.dispatchEvent(new CustomEvent('secretChanged'));
        refetch();
      } catch (error) {
        toast.error('Failed to delete secret');
      }
    }
  };

  if (labelLoading || secretsLoading) {
    return (
      <LabelDetailContainer>
        <LoadingSpinner />
      </LabelDetailContainer>
    );
  }

  if (labelError || secretsError) {
    return (
      <LabelDetailContainer>
        <EmptyState>
          <h3>Error loading label</h3>
          <p>There was an error loading the label details.</p>
          <Button onClick={() => navigate('/labels')}>
            Back to Labels
          </Button>
        </EmptyState>
      </LabelDetailContainer>
    );
  }

  if (!label) {
    return (
      <LabelDetailContainer>
        <EmptyState>
          <h3>Label not found</h3>
          <p>The label you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/labels')}>
            Back to Labels
          </Button>
        </EmptyState>
      </LabelDetailContainer>
    );
  }

  return (
    <LabelDetailContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/labels')}>
            <ArrowLeft size={16} />
            Back to Labels
          </BackButton>
          
          <LabelInfo>
            <LabelIcon $color={label.color}>
              <Tag size={20} />
            </LabelIcon>
            <LabelDetails>
              <h1>{label.name}</h1>
              <p>{secrets?.length || 0} secrets</p>
            </LabelDetails>
          </LabelInfo>
        </HeaderLeft>
        
        <Controls>
          <SearchInput
            placeholder="Search secrets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search size={16} />}
          />
          <FilterButton variant="outline" size="sm">
            <Filter size={16} />
            Filter
          </FilterButton>
        </Controls>
      </Header>

      {filteredSecrets.length === 0 ? (
        <EmptyState>
          <h3>No secrets found</h3>
          <p>
            {searchQuery 
              ? `No secrets match "${searchQuery}" in this label.`
              : 'This label doesn\'t have any secrets yet.'
            }
          </p>
          {!searchQuery && (
            <Button onClick={() => navigate('/spaces')}>
              <Plus size={16} />
              Add Secrets
            </Button>
          )}
        </EmptyState>
      ) : (
        <SecretsGrid>
          {filteredSecrets.map((secret) => (
            <SecretCard
              key={secret.id}
              secret={secret}
              onSecretClick={handleSecretClick}
              onEdit={handleEditSecret}
              onDelete={handleDeleteSecret}
              onCopyPassword={handleCopyPassword}
              onCopySecretKey={handleCopySecretKey}
            />
          ))}
        </SecretsGrid>
      )}
    </LabelDetailContainer>
  );
}

export default LabelDetailPage;
